import React, { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { studentAPI } from '@/lib/api';
import { Sparkles, RefreshCw, Loader2, AlertCircle, Image as ImageIcon } from 'lucide-react';
import { toast } from 'sonner';

const palette = { ink: '#2d2a26', mute: '#8c8780', indigo: '#6366f1', goldGlow: 'rgba(212,168,83,0.15)' };

const VirtualBackgroundsManager = ({ studentId, studentName, interests = [] }) => {
  const queryClient = useQueryClient();
  const [imageState, setImageState] = useState({}); // id -> 'loading'|'loaded'|'error'
  const previewMounted = useRef(false);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['student-virtual-backgrounds', studentId],
    queryFn: async () => (await studentAPI.getVirtualBackgrounds(studentId)).data,
    enabled: !!studentId,
    staleTime: 5 * 60 * 1000,
  });

  const backgrounds = data?.backgrounds || [];

  const regenerateMutation = useMutation({
    mutationFn: () => studentAPI.regenerateVirtualBackgrounds(studentId),
    onSuccess: (res) => {
      queryClient.setQueryData(['student-virtual-backgrounds', studentId], res.data);
      setImageState({});
      toast.success(`Generated 6 new wishful places for ${studentName}`);
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail || err?.message || 'Generation failed';
      toast.error(`Could not regenerate: ${detail}`);
    },
  });

  // Trigger an initial fetch the first time the section becomes visible so the
  // parent sees current art even if it has never been generated.
  useEffect(() => { previewMounted.current = true; }, []);

  const markState = (id, state) => setImageState((prev) => (prev[id] === state ? prev : { ...prev, [id]: state }));

  const hasError = data?.error;

  return (
    <div className="mt-4 mb-3" data-testid={`virtual-backgrounds-manager-${studentId}`}>
      <div className="flex items-center justify-between mb-2">
        <p className="text-xs font-bold uppercase flex items-center gap-1.5" style={{ color: palette.indigo }}>
          <Sparkles size={12} /> Wishful Video Backgrounds
        </p>
        <button
          onClick={() => regenerateMutation.mutate()}
          disabled={regenerateMutation.isPending}
          className="flex items-center gap-1 text-[11px] font-bold transition-all hover:scale-105 disabled:opacity-50"
          style={{ color: palette.indigo }}
          data-testid="regenerate-virtual-backgrounds"
        >
          {regenerateMutation.isPending
            ? <Loader2 size={11} className="animate-spin" />
            : <RefreshCw size={11} />}
          {regenerateMutation.isPending ? 'Generating...' : 'Generate new set'}
        </button>
      </div>

      <div className="p-3 rounded-lg" style={{ background: 'rgba(99,102,241,0.05)', border: '1px solid rgba(99,102,241,0.15)' }}>
        <p className="text-xs mb-3" style={{ color: palette.mute }}>
          Six AI-generated photo scenes built from {studentName}&apos;s interests
          {interests.length > 0 ? ` (${interests.slice(0, 4).join(', ')}${interests.length > 4 ? '…' : ''})` : ''}.
          The student picks one from the camera preview before recording read-aloud, and it composites behind their video.
        </p>

        {hasError && (
          <div className="mb-3 p-2 rounded-md flex items-start gap-2 text-xs" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid rgba(239,68,68,0.2)' }}>
            <AlertCircle size={14} style={{ color: '#EF4444' }} className="flex-shrink-0 mt-0.5" />
            <span style={{ color: '#7F1D1D' }}>{hasError}</span>
          </div>
        )}

        {isLoading && (
          <div className="flex items-center gap-2 text-xs py-6 justify-center" style={{ color: palette.mute }}>
            <Loader2 size={14} className="animate-spin" /> Loading current set...
          </div>
        )}

        {!isLoading && backgrounds.length === 0 && !hasError && (
          <div className="text-center py-6">
            <ImageIcon size={26} className="mx-auto mb-2" style={{ color: palette.mute }} />
            <p className="text-xs mb-3" style={{ color: palette.mute }}>
              No wishful places generated yet for {studentName}.
            </p>
            <button
              onClick={() => regenerateMutation.mutate()}
              disabled={regenerateMutation.isPending}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-bold transition-all hover:scale-105 disabled:opacity-50"
              style={{ background: palette.indigo, color: '#fff' }}
            >
              {regenerateMutation.isPending ? <Loader2 size={12} className="animate-spin" /> : <Sparkles size={12} />}
              Generate from interests
            </button>
          </div>
        )}

        {backgrounds.length > 0 && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {backgrounds.map((bg) => {
              const state = imageState[bg.id] || 'loading';
              return (
                <div key={bg.id}
                  className="rounded-md overflow-hidden"
                  style={{ background: '#1F2937', border: '1px solid rgba(0,0,0,0.1)' }}
                  data-testid={`bg-card-${bg.id}`}
                  title={bg.prompt}
                >
                  <div className="relative" style={{ aspectRatio: '16/9' }}>
                    <img
                      src={bg.image_url}
                      alt={bg.name}
                      className="absolute inset-0 w-full h-full object-cover"
                      onLoad={() => markState(bg.id, 'loaded')}
                      onError={() => markState(bg.id, 'error')}
                      style={{ opacity: state === 'loaded' ? 1 : 0, transition: 'opacity 0.3s' }}
                      data-testid={`bg-thumb-${bg.id}`}
                    />
                    {state !== 'loaded' && (
                      <div className="absolute inset-0 flex items-center justify-center text-white text-xs gap-1.5">
                        {state === 'error' ? (
                          <>
                            <AlertCircle size={14} /> Couldn&apos;t render
                          </>
                        ) : (
                          <>
                            <Loader2 size={14} className="animate-spin" /> Generating…
                          </>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="px-2 py-1.5">
                    <p className="text-xs font-bold truncate" style={{ color: '#fff' }}>{bg.name}</p>
                    <p className="text-[10px] truncate" style={{ color: 'rgba(255,255,255,0.55)' }}>{bg.prompt}</p>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {!isLoading && backgrounds.length > 0 && (
          <p className="text-[10px] mt-2.5" style={{ color: palette.mute }}>
            First-time generation can take 10–30 seconds per image. Hover any tile to see the full prompt.
          </p>
        )}

        {isFetching && !isLoading && !regenerateMutation.isPending && (
          <p className="text-[10px] mt-2 flex items-center gap-1" style={{ color: palette.mute }}>
            <Loader2 size={10} className="animate-spin" /> Refreshing…
          </p>
        )}
      </div>
    </div>
  );
};

export default VirtualBackgroundsManager;
